import React, { useState, useEffect } from 'react';
import { Calendar, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { parseISO, isValid, addDays } from 'date-fns';
import { enUS, fr } from 'date-fns/locale';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { InfoTooltip } from '@/components/common/InfoTooltip';
import { TimezoneDropdown } from '@/components/common/TimezoneDropdown';
import { toZonedTime, format as formatWithTimezone } from 'date-fns-tz';

// Map locales for easy access
const locales = { en: enUS, fr: fr };

// Helper function to get a display-friendly timezone name
const getDisplayTimezoneName = (ianaTimezone: string): string => {
  if (!ianaTimezone) return '';
  const parts = ianaTimezone.split('/');
  const cityName = parts[parts.length - 1];
  return cityName.replace(/_/g, ' '); // Replace underscores with spaces
};

interface ScheduleCardProps {
  initialScheduledAtUtc: string | null; // Renamed from scheduledAt
  onScheduleChange: (newScheduledAtUtc: string | null, timezone?: string) => void;
}

export const ScheduleCard: React.FC<ScheduleCardProps> = ({
  initialScheduledAtUtc,
  onScheduleChange
}) => {
  const { t, i18n } = useTranslation();
  
  // Attempt to get browser timezone, fallback to UTC
  const [selectedTimezone, setSelectedTimezone] = useState<string>(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (e) {
      return 'UTC';
    }
  });

  // State for the toggle switch itself
  const [isToggleActive, setIsToggleActive] = useState(Boolean(initialScheduledAtUtc));
  // State for the date currently shown in the DatePicker, zoned to selectedTimezone
  const [currentPickerDate, setCurrentPickerDate] = useState<Date | null>(null);

  // Effect to initialize toggle and picker date based on initialScheduledAtUtc prop changes.
  // Crucially, this effect should NOT re-run and reset isToggleActive if only selectedTimezone changes.
  useEffect(() => {
    const hasInitialSchedule = Boolean(initialScheduledAtUtc);
    console.log('📅 ScheduleCard - useEffect - initialScheduledAtUtc:', initialScheduledAtUtc, 'hasInitialSchedule:', hasInitialSchedule);
    setIsToggleActive(hasInitialSchedule);

    if (hasInitialSchedule && initialScheduledAtUtc) {
      try {
        const parsedUtc = parseISO(initialScheduledAtUtc);
        console.log('📅 ScheduleCard - useEffect - parsedUtc:', parsedUtc, 'isValid:', isValid(parsedUtc));
        if (isValid(parsedUtc)) {
          // When initialScheduledAtUtc changes, zone it to the current selectedTimezone for display
          const zonedDate = toZonedTime(parsedUtc, selectedTimezone);
          console.log('📅 ScheduleCard - useEffect - zonedDate for timezone', selectedTimezone, ':', zonedDate);
          setCurrentPickerDate(zonedDate);
        } else {
          setCurrentPickerDate(null); // Invalid initial date
        }
      } catch (error) {
        console.error("Error parsing initialScheduledAtUtc in ScheduleCard:", error);
        setCurrentPickerDate(null);
      }
    } else {
      // No initial schedule from props. If the toggle is consequently off, clear the picker.
      // If the user had manually turned the toggle ON for a new post, currentPickerDate would be set by handleToggleScheduleClick.
      if (!hasInitialSchedule) { // i.e., if isToggleActive is now false due to no initial schedule
        setCurrentPickerDate(null);
      }
    }
  }, [initialScheduledAtUtc]); // Key Change: Only depends on initialScheduledAtUtc.
                               // selectedTimezone changes are handled by handleTimezoneChange for an active picker.

  // Converts a Date object to a UTC ISO string ending with 'Z'.
  // currentPickerDate is expected to be a Date object whose internal UTC value
  // correctly represents the selected wall-clock time in selectedTimezone.
  const convertDateToUtcIsoZ = (date: Date | null): string | null => {
    if (!date || !isValid(date)) return null;
    return date.toISOString(); // Standard JS Date method, always returns UTC with Z
  };

  const handleToggleScheduleClick = () => {
    const newToggleState = !isToggleActive;
    setIsToggleActive(newToggleState);

    if (newToggleState) { // Toggle just turned ON
      let dateToSubmitToParent: Date | null = currentPickerDate;
      
      // If picker is empty when turning ON, try to use initial prop or set a default
      if (!dateToSubmitToParent) {
        if (initialScheduledAtUtc) { // If there was an original schedule
             try {
                const parsed = parseISO(initialScheduledAtUtc);
                if (isValid(parsed)) dateToSubmitToParent = toZonedTime(parsed, selectedTimezone);
             } catch {}
        }
        if (!dateToSubmitToParent) { // Still no date, set a new default
            const nowPlus1Hour = toZonedTime(addDays(new Date(), 0), selectedTimezone);
            nowPlus1Hour.setHours(nowPlus1Hour.getHours() + 1);
            nowPlus1Hour.setMinutes(Math.ceil(nowPlus1Hour.getMinutes() / 15) * 15);
            setCurrentPickerDate(nowPlus1Hour); // Update picker display
            dateToSubmitToParent = nowPlus1Hour;
        } else {
            setCurrentPickerDate(dateToSubmitToParent); // Ensure picker displays this re-instated date
        }
      }
      // Ensure a valid date before calling onScheduleChange
      if (dateToSubmitToParent && isValid(dateToSubmitToParent)) {
        onScheduleChange(convertDateToUtcIsoZ(dateToSubmitToParent), selectedTimezone);
      } else {
        // If, after all logic, dateToSubmitToParent is null or invalid, treat as clearing the schedule intent
        const defaultDate = toZonedTime(addDays(new Date(), 0), selectedTimezone);
        defaultDate.setHours(defaultDate.getHours() + 1);
        defaultDate.setMinutes(Math.ceil(defaultDate.getMinutes() / 15) * 15);
        setCurrentPickerDate(defaultDate);
        onScheduleChange(convertDateToUtcIsoZ(defaultDate), selectedTimezone);
      }
    } else { // Toggle just turned OFF
      onScheduleChange(null, selectedTimezone); // Signal to parent: no active scheduling intent
    }
  };

  const handleDateChange = (date: Date | null) => { // Called by DatePicker
    if (!isToggleActive) return; // Should be disabled, but good check

    if (date && isValid(date)) {
      // 'date' from DatePicker is a JS Date object representing the selected wall-clock time
      // in the browser's local timezone.
      // We need to interpret these wall-clock numbers as being in the 'selectedTimezone'.

      const year = date.getFullYear();
      const month = date.getMonth(); // 0-indexed
      const day = date.getDate();
      const hours = date.getHours();
      const minutes = date.getMinutes();
      // Seconds from picker are usually 0 or not relevant for this level of precision,
      // but we can include them if available.
      const seconds = date.getSeconds(); 

      // Format these wall-clock parts into an ISO-like string.
      // This string is timezone-agnostic at this point.
      const wallClockString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
      
      // Convert this wall-clock string to a true UTC Date object,
      // interpreting it as if it were in the 'selectedTimezone'.
      const dateInSelectedTimezone = toZonedTime(wallClockString, selectedTimezone);

      setCurrentPickerDate(dateInSelectedTimezone); // Update state with the correct UTC instant
      onScheduleChange(convertDateToUtcIsoZ(dateInSelectedTimezone), selectedTimezone);
    } else {
      setCurrentPickerDate(null);
      onScheduleChange(null, selectedTimezone); // Date cleared or invalid
    }
  };

  const handleTimezoneChange = (timezone: string) => {
    setSelectedTimezone(timezone);
    
    // No date conversions - timezone change doesn't affect date/time
    // Only notify parent about the timezone change while keeping same UTC time
    if (isToggleActive && currentPickerDate) {
      onScheduleChange(convertDateToUtcIsoZ(currentPickerDate), timezone);
    }
  };
  
  // Format the date for display using locale and selected timezone
  const currentLocale = locales[i18n.resolvedLanguage as keyof typeof locales] || enUS;
  let formattedDateTimeString = '';

  // Determine what date to use for the "Scheduled for..." status line display
  const dateForStatusLineDisplay = isToggleActive
    ? currentPickerDate // If toggle is ON, use the live picker date
    : initialScheduledAtUtc // If toggle is OFF, use the original schedule from parent (if any)
      ? toZonedTime(parseISO(initialScheduledAtUtc), selectedTimezone)
      : null;

  if (dateForStatusLineDisplay && isValid(dateForStatusLineDisplay)) {
    try {
      const justDateAndTime = formatWithTimezone(dateForStatusLineDisplay, 'PPpp', { locale: currentLocale, timeZone: selectedTimezone });
      const displayTimezoneName = getDisplayTimezoneName(selectedTimezone);
      
      // Use the updated translation key and pass both date and timezoneName
      // For English, the default string includes "time"
      const defaultTranslation = `Scheduled for {{date}} ({{timezoneName}} time)`;
      formattedDateTimeString = t('cards.schedule.statusScheduled', defaultTranslation, {
         date: justDateAndTime,
         timezoneName: displayTimezoneName
      });

    } catch(e) {
      console.error("Error formatting display date-time:", e);
      formattedDateTimeString = t('common.error', 'Error');
    }
  }

  return (
    <div className="bg-white rounded-xl border border-black shadow-lg p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Calendar className="w-5 h-5 text-gray-400" />
          <h2 className="text-lg font-semibold text-gray-900">{t('cards.schedule.title', 'Schedule Post')}</h2>
        </div>
        <InfoTooltip message={t('tooltips.scheduleCard', 'Schedule your post for a future date and time. If no date is set, the post can be published immediately or saved as a draft.')} />
      </div>

      {/* Schedule Toggle Section */}
      <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
        <div>
          <h3 className="text-sm font-medium text-gray-900">{t('cards.schedule.toggleLabel', 'Planifier pour plus tard')}</h3>
          <p className="text-xs text-gray-500 max-w-xs">{t('cards.schedule.toggleDescription', 'Choisissez quand publier. La suggestion de l\'IA pour votre marque peut vous aider.')}</p>
        </div>
        <button
          type="button"
          onClick={handleToggleScheduleClick}
          className={`${isToggleActive ? 'bg-primary-600' : 'bg-gray-200'} relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500`}
          role="switch"
          aria-checked={isToggleActive}
        >
          <span className="sr-only">{t('cards.schedule.toggleLabel', 'Schedule for later')}</span>
          <span
            aria-hidden="true"
            className={`${isToggleActive ? 'translate-x-5' : 'translate-x-0'} pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200`}
          />
        </button>
      </div>

      {/* Date, Time, and Timezone Pickers - Always rendered, disabled based on isToggleActive */}
      <div className="space-y-4 mb-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="schedule-date-picker" className={`block text-xs font-medium mb-1 ${!isToggleActive ? 'text-gray-400' : 'text-gray-700'}`}>{t('common.date', 'Date')}</label>
            <DatePicker
              id="schedule-date-picker"
              selected={currentPickerDate}
              onChange={handleDateChange}
              dateFormat={currentLocale.code === 'fr' ? 'dd/MM/yyyy' : 'MM/dd/yyyy'}
              className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${!isToggleActive ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'border-gray-300'}`}
              placeholderText={t('cards.schedule.datePlaceholder', 'Select date')}
              minDate={toZonedTime(new Date(), selectedTimezone)}
              locale={currentLocale}
              disabled={!isToggleActive}
              calendarClassName="custom-datepicker-theme"
            />
          </div>
          <div>
            <label htmlFor="schedule-time-picker" className={`block text-xs font-medium mb-1 ${!isToggleActive ? 'text-gray-400' : 'text-gray-700'}`}>{t('common.time', 'Time')}</label>
            <DatePicker
              id="schedule-time-picker"
              selected={currentPickerDate}
              onChange={handleDateChange}
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={15}
              timeCaption={t('common.time', 'Time')}
              dateFormat="p"
              className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${!isToggleActive ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'border-gray-300'}`}
              placeholderText={t('cards.schedule.timePlaceholder', 'Select time')}
              locale={currentLocale}
              disabled={!isToggleActive}
              calendarClassName="custom-datepicker-theme"
            />
          </div>
        </div>
        
        <div>
          <label htmlFor="schedule-timezone-picker" className={`block text-xs font-medium mb-1 ${!isToggleActive ? 'text-gray-400' : 'text-gray-700'}`}>{t('common.timezoneDropdown.timezoneLabel', 'Fuseau horaire')}</label>
          <TimezoneDropdown 
            selectedTimezone={selectedTimezone}
            onTimezoneChange={handleTimezoneChange}
            disabled={!isToggleActive}
          />
        </div>
      </div>

      {/* Publishing Status Section */}
      <div className="pt-4 border-t border-gray-200">
        <h4 className="text-sm font-medium text-gray-700 mb-2">{t('cards.schedule.statusTitle', 'Publishing Status')}</h4>
        <div className="flex items-center gap-2 text-sm">
          <Clock className="w-4 h-4 text-gray-400" />
          {(isToggleActive || initialScheduledAtUtc) && dateForStatusLineDisplay ? (
            <span className={isToggleActive ? "text-primary-600" : "text-gray-600"}>
              {formattedDateTimeString}
            </span>
          ) : (
            <span className="text-gray-600">
              {t('cards.schedule.statusImmediate', 'Will be published immediately')}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};